import { useState } from "react";
import { motion } from "framer-motion";
import { GATES } from "@/data/temple";
import { Eyebrow, Reveal } from "./Reveal";
import { ease } from "@/lib/scroll";
import { Link } from "react-router-dom";
import { ArrowUpRight, Lock, Unlock } from "lucide-react";
import { slugify } from "@/lib/intro";
import { useAuth } from "@/lib/auth";

const Access = ({ i, initiate }) => {
  if (initiate) return <Unlock data-testid={`gate-unlocked-${i + 1}`} className="w-4 h-4 text-[#D9B47A] shrink-0" aria-label="Unlocked" />;
  if (i === 4) return <span className="font-tt-mono text-[10px] uppercase tracking-[0.2em] text-[#C2414F] border border-[#C2414F]/60 rounded-full px-2 py-0.5 shrink-0">Free</span>;
  return <Lock className="w-3.5 h-3.5 text-[#8F8176] shrink-0" aria-label="Ritual for initiates" />;
};

const ReadLink = ({ g, i, mobile = false }) => (
  <Link
    to={`/gates/${slugify(g.name)}`}
    data-testid={`gate-read-link-${mobile ? "mobile-" : ""}${i + 1}`}
    className="group mt-8 inline-flex items-center gap-3 rounded-full border border-[#D9B47A]/60 px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#F2E8DF] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors duration-300"
  >
    Enter Gate {String(i + 1).padStart(2, "0")}
    <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
  </Link>
);

const pad = (n) => String(n).padStart(2, "0");

const Details = ({ g }) => (
  <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-6 mt-8">
    {[
      ["Primary inputs", g.inputs],
      ["Supporting vessels", g.vessels],
      ["Primary output", g.output],
      ["Core quality", g.quality],
    ].map(([k, v]) => (
      <div key={k} className="border-t border-[#D9B47A]/20 pt-4">
        <dt className="font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#D9B47A]">{k}</dt>
        <dd className="mt-2 text-[15px] text-[#E4D8CC] leading-relaxed">{v}</dd>
      </div>
    ))}
  </dl>
);

const Panel = ({ g, i }) => (
  <motion.div
    key={i}
    initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    transition={{ duration: 0.8, ease }}
    data-testid="gate-detail-panel"
  >
    <div className="flex items-end gap-6">
      <span className="font-display italic text-[9rem] leading-[0.8] text-[#D9B47A]">{pad(i + 1)}</span>
      <span className="font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#A8998C] pb-3">Gate of {GATES.length}</span>
    </div>
    <h3 className="font-display text-5xl mt-8">{g.name}</h3>
    <p className="mt-5 text-lg text-[#CFC2B6] leading-relaxed max-w-xl">{g.line}</p>
    <Details g={g} />
    <ReadLink g={g} i={i} />
  </motion.div>
);

export default function Gates() {
  const [active, setActive] = useState(0);
  const { user } = useAuth();
  return (
    <section id="gates" data-testid="gates-section" className="relative bg-[#0D0908] border-y border-[#D9B47A]/10">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10 py-28 sm:py-40">
        <Reveal>
          <Eyebrow n="II">The 13 Gates to Ecstasy</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl mt-8 max-w-4xl leading-[1.02]">
            An ascension map, from the <em className="text-[#D9B47A]">root</em> to the waking life.
          </h2>
          <p className="mt-6 max-w-xl text-base sm:text-lg text-[#A8998C]">
            Each gate is an orgasmic state with its own inputs, vessels, output and quality. Touch a gate to open it.
          </p>
        </Reveal>

        <div className="mt-20 grid lg:grid-cols-12 gap-12">
          <ol className="lg:col-span-5 relative">
            <span className="absolute left-[15px] top-2 bottom-2 w-px bg-gradient-to-b from-[#C2414F] via-[#D9B47A]/50 to-[#F2E8DF]/30" />
            {GATES.map((g, i) => {
              const on = i === active;
              return (
                <li key={g.name} className="relative">
                  <button
                    data-testid={`gate-item-${i + 1}`}
                    onClick={() => setActive(i)}
                    onMouseEnter={() => window.matchMedia("(hover: hover)").matches && setActive(i)}
                    className="w-full text-left flex items-center gap-6 py-4 group"
                  >
                    <span className={`relative z-10 w-[31px] h-[31px] rounded-full border flex items-center justify-center font-tt-mono text-[11px] transition-colors duration-300 ${on ? "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]" : "bg-[#0D0908] border-[#D9B47A]/40 text-[#D9B47A]"}`}>
                      {pad(i + 1)}
                    </span>
                    <span className={`font-display text-2xl sm:text-3xl transition-[color,transform] duration-500 ${on ? "text-[#F2E8DF] translate-x-2" : "text-[#9C8E82] group-hover:text-[#E4D8CC]"}`}>
                      {g.name}
                    </span>
                    <Access i={i} initiate={Boolean(user?.initiate)} />
                  </button>
                  {on && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="lg:hidden pl-[55px] pb-6 overflow-hidden"
                      data-testid={`gate-mobile-detail-${i + 1}`}
                    >
                      <p className="text-[15px] text-[#CFC2B6] leading-relaxed">{g.line}</p>
                      <Details g={g} />
                      <ReadLink g={g} i={i} mobile />
                    </motion.div>
                  )}
                </li>
              );
            })}
          </ol>
          <div className="hidden lg:block lg:col-span-7">
            <div className="sticky top-32">
              <Panel g={GATES[active]} i={active} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
